import { useState } from 'react';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '../../utils/utils';
import LabelWithHelp from '../LabelWithHelp';

interface SelectFieldProps {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    optionLabels?: Record<string, string>;
    disabled?: boolean;
    label: string;
    placeholder?: string;
    noOptions?: string;
    error?: boolean;
    /** Show a spinner in the trigger and block interaction while options are
     *  being fetched. Mirrors the loading state the MUI-based
     *  `MultiSelectComponent` exposed, so availability-driven selects keep
     *  their feedback after migrating off MUI. */
    loading?: boolean;
    className?: string;
    /** Optional extra classes appended to the popover Select.Content.
     *  Onboarding uses this to anchor the dropdown to the trigger width.
     *  Other callers can leave this unset to keep existing behavior. */
    contentClassName?: string;
    /** Optional override for the Select.Viewport classes. Use this to
     *  expand the dropdown's max-height beyond the default `max-h-60`
     *  when many options are present. Defaults to `'p-1 max-h-60'`. */
    viewportClassName?: string;
    /** Render the label as a separate block above the trigger rather than
     *  the floating-label-inside pattern. Matches the convention used in
     *  newer admin-ui sections. */
    labelAbove?: boolean;
    /** Short explanation rendered as an (i) tooltip next to the label, via the
     *  shared `LabelWithHelp`. Use it only where the field is genuinely
     *  non-obvious. Only honoured in `labelAbove` mode. */
    help?: string;
}

const SelectField = ({
    value,
    onChange,
    options,
    optionLabels,
    disabled = false,
    label,
    placeholder,
    noOptions,
    error = false,
    loading = false,
    className,
    contentClassName,
    viewportClassName,
    labelAbove = false,
    help,
}: SelectFieldProps) => {
    const [open, setOpen] = useState(false);
    const isFloating = open || !!value;
    // Radix throws on a `<Select.Item value="">` while rendering — even with
    // the menu closed — and nothing above catches it, so one empty option
    // turned the whole app white (the standard product category's payment
    // flow). An empty value already means "nothing chosen" and shows the
    // placeholder, so an empty option is dropped here rather than at each
    // caller, including options derived from data.
    const items = options.filter((option) => option !== '');

    const root = (
        <Select.Root
            value={value || undefined}
            onValueChange={onChange}
            disabled={disabled || loading}
            open={open}
            onOpenChange={setOpen}
        >
            <Select.Trigger
                className={cn(
                    'flex items-center justify-between gap-2 py-2 px-4 rounded-xl bg-transparent',
                    'border border-solid text-foreground transition-all outline-none w-full text-left',
                    labelAbove ? '' : 'data-[placeholder]:text-transparent',
                    error
                        ? 'border-red-500'
                        : open ? 'border-primary' : 'border-foreground',
                    (disabled || loading) && 'cursor-not-allowed',
                )}
            >
                <span className={'truncate'}>
                    <Select.Value placeholder={placeholder ?? (labelAbove ? '' : label)} />
                </span>
                {loading ? (
                    <Loader2 className={'h-4 w-4 shrink-0 text-foreground/60 animate-spin'} />
                ) : (
                    <Select.Icon>
                        <ChevronDown
                            className={cn(
                                'h-4 w-4 shrink-0 text-foreground/60 transition-transform duration-200',
                                open && 'rotate-180',
                            )}
                        />
                    </Select.Icon>
                )}
            </Select.Trigger>
            <Select.Portal>
                <Select.Content
                    className={cn(
                        'overflow-hidden rounded-xl border border-foreground/20 bg-white shadow-lg',
                        // Match the trigger's width instead of sizing to the
                        // content, which rendered short values (times, durations)
                        // as a narrow column floating over the fields below.
                        'z-[9999] min-w-[var(--radix-select-trigger-width)]',
                        contentClassName,
                    )}
                    position={'popper'}
                    sideOffset={4}
                >
                    <Select.Viewport className={viewportClassName ?? 'p-1 max-h-60'}>
                        {items.length === 0 ? (
                            <div className={'px-4 py-2 text-sm text-foreground/50'}>
                                {noOptions ?? 'No options available'}
                            </div>
                        ) : (
                            items.map((option) => (
                                <Select.Item
                                    key={option}
                                    value={option}
                                    className={cn(
                                        'flex items-center justify-between px-4 py-2 rounded-lg text-sm',
                                        'text-foreground cursor-pointer outline-none',
                                        'data-[highlighted]:bg-primary/10',
                                        'data-[state=checked]:font-medium',
                                    )}
                                >
                                    <Select.ItemText>{optionLabels?.[option] ?? option}</Select.ItemText>
                                    <Select.ItemIndicator>
                                        <Check className={'h-4 w-4 text-primary'} />
                                    </Select.ItemIndicator>
                                </Select.Item>
                            ))
                        )}
                    </Select.Viewport>
                </Select.Content>
            </Select.Portal>
        </Select.Root>
    );

    if (labelAbove) {
        return (
            <div className={cn('flex flex-col gap-1.5', className)}>
                {help ? (
                    <LabelWithHelp text={label} help={help} className={'mb-0'} />
                ) : (
                    <label className={'text-sm text-foreground/70 font-medium'}>
                        {label}
                    </label>
                )}
                {root}
            </div>
        );
    }

    return (
        <div className={cn('relative', className)}>
            {root}
            <label
                className={cn(
                    'absolute left-3 pointer-events-none transition-all duration-200',
                    'truncate max-w-[calc(100%-2.5rem)]',
                    isFloating
                        ? '-top-2.5 text-xs bg-white px-1 text-foreground'
                        : 'top-1/2 -translate-y-1/2 text-sm text-foreground',
                )}
            >
                {label}
            </label>
        </div>
    );
};

export default SelectField;
